import { ActiveLink } from '@components/common/navigation/ActiveNavLink';
import { SimpleSpinner } from '@components/common/loading/SimpleSpinner';
import { CourseUnitsDataQuery } from '@graphql/generated-graphql';
import { useCourseUnitsFixed } from '@hooks/courses/useCourseActivities';
import { useQueryParams } from '@hooks/useQueryParams';
import { urlTag } from '@utils/urlPaths';
import React, { useEffect, useState } from 'react';
import { useCourseUnitsMenuDispatcher } from './CourseUnitsMenu';
import { ChevronRightIcon } from '@heroicons/react/outline';
import { useCourseTermContext } from '@components/context/CourseContext';
import { CourseTermsListDropdown } from '@components/course/CourseTermsListDropdown';
import { Text } from '@skipper/ui';
import { useRouter } from 'next/router';

export function UnitsMenuList() {
    const { id } = useQueryParams(['id']);
    const router = useRouter();
    const { selectedTerm, setSelectedTerm, showTerms, termList } = useCourseTermContext();
    const [redirect, setRedirect] = useState(false);
    const { data: units, isLoading } = useCourseUnitsFixed({
        variables: {
            includeActivities: true,
            includeDraft: false,
            includeHidden: false,
            onlyVisible: true,
        },
    });

    useEffect(() => {
        if (redirect && !isLoading && !!units) {
            setRedirect(false);
            router.push(`/courses/${id}/units/${units?.[0]?.id}`);
        }
    }, [redirect, isLoading, units]);

    return (
        <div className="relative flex flex-col border-t h-full w-full min-w-full">
            <div className="border-gray-100 border-b py-4 px-4 flex justify-between items-center">
                <div className="flex text-passport text-sm font-bold">UNITS</div>
                <div className="flex items-center justify-end w-full">
                    {showTerms && (
                        <CourseTermsListDropdown
                            setSelectedTerm={setSelectedTerm}
                            courseTerm={selectedTerm}
                            termsList={termList?.filter((t) => !!t?.published)}
                            onTermChange={() => {
                                setRedirect(true);
                            }}
                        />
                    )}
                </div>
            </div>
            <div className="flex flex-col flex-grow h-full gap-y-1 px-2 overflow-y-auto pt-1 w-full">
                {isLoading && <SimpleSpinner />}
                {!isLoading && units?.length === 0 && (
                    <div className="w-full py-1 flex items-center justify-center text-center">
                        <Text>No units</Text>
                    </div>
                )}
                {units?.map((u) => {
                    return <UnitMenuItem key={u.id} {...u} courseId={id as string} />;
                })}
            </div>
        </div>
    );
}

function UnitMenuItem({
    title,
    activities,
    active,
    id,
    courseId,
}: { active?: boolean; courseId: string } & NonNullable<
    NonNullable<CourseUnitsDataQuery['courses']>[0]['units']
>[0]) {
    const dispatcher = useCourseUnitsMenuDispatcher();
    const route = urlTag`/courses/${'id'}/units/${'unitId'}`;

    return (
        <ActiveLink href={route({ id: courseId, unitId: id })} isFuzzyMatch>
            <div className="relative w-full h-full flex justify-between">
                <div className="flex items-center py-4 pl-2 pr-1 truncate text-sm">
                    <div className="h-5 w-8 bg-teal-200 text-teal-700 rounded-full flex items-center justify-center mr-2 shadow-sm">
                        {activities?.length ?? 0}
                    </div>
                    <div className="w-full truncate text-passport text-sm">{title}</div>
                </div>
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        dispatcher.setUnit(id);
                    }}
                    className="flex items-center justify-end px-1">
                    <ChevronRightIcon className="w-5 h-6" />
                </button>
            </div>
        </ActiveLink>
    );
}
